import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CompletedContainer } from '../interfaces/mock.interface';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { CheckCircle2, Clock, ArrowRight } from 'lucide-react';

interface CompletedContainersTableProps {
  containers: CompletedContainer[];
}

export const CompletedContainersTable = ({
  containers,
}: CompletedContainersTableProps) => {
  const getTransitTimeBadge = (days: number) => {
    if (days <= 25) {
      return (
        <Badge variant="default" className="bg-green-600">
          {days} zile
        </Badge>
      );
    } else if (days <= 35) {
      return <Badge variant="secondary">{days} zile</Badge>;
    } else {
      return (
        <Badge variant="outline" className="text-orange-600 border-orange-600">
          {days} zile
        </Badge>
      );
    }
  };

  if (containers.length === 0) {
    return (
      <Card className="w-full h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Containere Finalizate Recent
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Nu există containere finalizate în ultimele 30 de zile.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5" />
          Containere Finalizate Recent
          <Badge variant="outline" className="ml-2">
            {containers.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="rounded-md border flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nr. Container</TableHead>
                <TableHead>BL / Booking</TableHead>
                <TableHead>Rută</TableHead>
                <TableHead>Data Finalizare</TableHead>
                <TableHead>Timp Tranzit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {containers.map(container => (
                <TableRow key={container.id}>
                  <TableCell className="font-mono font-medium">
                    {container.containerNumber}
                    <div className="text-xs text-muted-foreground mt-1">
                      {container.size}' {container.type}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-mono text-sm">
                        {container.billNumber}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {container.bookingNumber}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="font-mono">{container.originPort}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono">
                        {container.destinationPort}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span>
                        {format(new Date(container.completedDate), 'dd MMM yyyy', {
                          locale: ro,
                        })}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getTransitTimeBadge(container.transitTime)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
